import React, { useState, useEffect, useRef } from 'react'
import { Mic, Upload, Link2, Loader2, Check, Trash2, X } from 'lucide-react'
import { voiceApi } from '../api/client'

interface VoiceProfileCardProps {
  /** Fires whenever a voice reference is set/cleared so the panel can adapt. */
  onVoiceChange?: (hasVoice: boolean) => void
}

/**
 * "Your narration voice" — clone the creator's voice so generated voiceovers
 * sound like them. Set it by uploading a short sample or pulling from one of
 * their video URLs. Shown at the top of the Voiceover panel.
 */
export function VoiceProfileCard({ onVoiceChange }: VoiceProfileCardProps) {
  const [hasVoice, setHasVoice] = useState(false)
  const [label, setLabel] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'upload' | 'video' | 'delete' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showVideo, setShowVideo] = useState(false)
  const [videoUrl, setVideoUrl] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const applyState = (has: boolean, lbl?: string) => {
    setHasVoice(has)
    setLabel(lbl || '')
    onVoiceChange?.(has)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const d = await voiceApi.getVoiceProfile()
        if (!cancelled) applyState(!!d.has_voice, d.label)
      } catch {
        // treat as none
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) e.target.value = ''
    if (!file) return
    setBusy('upload'); setError(null)
    try {
      const d = await voiceApi.uploadVoiceSample(file)
      applyState(true, d.label)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Upload failed. Try a short WAV or MP3.')
    } finally {
      setBusy(null)
    }
  }

  const cloneFromVideo = async () => {
    const url = videoUrl.trim()
    if (!url) return
    setBusy('video'); setError(null)
    try {
      const d = await voiceApi.cloneVoiceFromVideo({ url })
      applyState(true, d.label)
      setShowVideo(false); setVideoUrl('')
    } catch (err: any) {
      const status = err?.response?.status
      setError(
        status === 502 ? 'Couldn’t fetch audio from that link — try uploading a sample instead.'
        : err?.response?.data?.error || 'Extraction failed. Try uploading a sample instead.'
      )
    } finally {
      setBusy(null)
    }
  }

  const removeVoice = async () => {
    setBusy('delete'); setError(null)
    try {
      await voiceApi.deleteVoiceProfile()
      applyState(false)
      setShowVideo(false)
    } catch {
      setError('Could not remove voice.')
    } finally {
      setBusy(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 bg-gray-700/40 rounded-lg px-3 py-2 text-xs text-gray-400 mb-2">
        <Loader2 size={13} className="animate-spin" /> Checking your voice…
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-lg p-2.5 mb-2">
      <input ref={fileRef} type="file" accept="audio/*,video/*" className="hidden" onChange={onFilePicked} />

      {hasVoice ? (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1 bg-indigo-500/20 rounded"><Check size={13} className="text-indigo-300" /></div>
            <div className="min-w-0">
              <div className="text-xs font-medium text-white truncate">Voice ready · {label || 'your voice'}</div>
              <div className="text-[10px] text-gray-500">Narration will sound like you</div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy !== null}
              className="text-[11px] text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700 disabled:opacity-50"
            >
              {busy === 'upload' ? <Loader2 size={12} className="animate-spin" /> : 'Replace'}
            </button>
            <button
              onClick={removeVoice}
              disabled={busy !== null}
              title="Remove voice"
              className="p-1 text-gray-400 hover:text-red-400 rounded hover:bg-gray-700 disabled:opacity-50"
            >
              {busy === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={13} />}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Mic size={13} className="text-indigo-300" />
            <span className="text-xs font-medium text-white">Clone your voice</span>
          </div>
          <p className="text-[11px] text-gray-400 mb-2 leading-relaxed">
            Add a 10–20s sample and every voiceover speaks in your voice.
          </p>
          <div className="flex gap-1.5">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy !== null}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {busy === 'upload' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Upload sample
            </button>
            <button
              onClick={() => { setShowVideo(!showVideo); setError(null) }}
              disabled={busy !== null}
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 bg-gray-700 text-gray-200 text-xs rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              title="Pull from a video URL"
            >
              <Link2 size={12} /> From a video
            </button>
          </div>

          {showVideo && (
            <div className="flex gap-1.5 mt-2">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && cloneFromVideo()}
                placeholder="Paste a video URL of you talking…"
                className="flex-1 px-2 py-1.5 bg-gray-900 border border-gray-600 rounded-lg text-white text-xs placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              />
              <button
                onClick={cloneFromVideo}
                disabled={!videoUrl.trim() || busy !== null}
                className="px-2.5 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {busy === 'video' ? <Loader2 size={12} className="animate-spin" /> : 'Use'}
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-1.5 mt-2 text-[11px] text-red-300">
          <X size={12} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}
    </div>
  )
}
